import Vue from 'vue';
import { getDomain } from '@/store/auth/auth.store';
import { AccessTokenKey, AUTH_STATE_COOKIE } from '@/store/auth/auth.store.constants';
import CryptoJS from 'crypto-js';

const COMMUNITY_KEYS = {
  INTERCONTINENTAL_ALLIANCE: '6725a490-2547-11ee-a8ab-dfc8db3ddec2'
}

// Helper function to generate CSRF token
function generateRandomToken(length = 32) {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
}

export const authHandler = {
  methods: {
    /**
     * Sets the authentication token and handles redirection
     * @param {string} loginResponse - The authentication response
     * @param {number} expiryDays - Number of days until token expires (default: 7)
     * Used in Login, LoginSSO and Register views
     */
    async handleAuthSuccess(loginResponse, expiryDays = 7, fromRegister = false) {
      console.log('handleAuthSuccess() START');
      console.log('loginResponse:', loginResponse);
      console.log('loginResponse.user:', loginResponse.user);
      try {
        // Define isNexos at the beginning of the function
        const isNexos = process.env.VUE_APP_CUSTOMER === 'nexos';

        // The new login API returns an object with message: 'OK' and sessionInfo: { ... } and other information
        // The old login API returns a plain text response with the token
        // This is done to improve the cybersecurity of the login process, specially for Nexos which is a bank
        const loginResponseIsObjectWithMessageOK = typeof loginResponse === 'object' && loginResponse.message === 'OK';

        // Handle "OK" response differently
        if (loginResponseIsObjectWithMessageOK) {
          // console.log('loginResponse is an object and message is OK');
          
          // Store session information in Vuex store
          if (loginResponse.sessionInfo) {
            // console.log('loginResponse session Info:', loginResponse.sessionInfo);
            // Check if there are active sessions
            if (loginResponse.sessionInfo.potentialSecurityRisk) {
              // Show notification about active sessions
              const message = isNexos 
                ? `Encontramos que tenías otra sesión activa. Por tu seguridad la cerramos, disfruta Nexos con confianza, desde donde estés. Te contamos más detalles por correo.` 
                : `You had another active session that has been closed. If the device was different from this one, you will receive an email with the details.`;
              const title = isNexos ? 'Sesión activa' : 'Active session';
              
              this.$bvToast.toast(message, {
                title: title,
                variant: 'warning',
                solid: true,
                autoHideDelay: 5000
              });

              // Wait for 5 seconds before redirecting
              await new Promise(resolve => setTimeout(resolve, 5000));
            }

            // For Nexos, check if this is a new session while another is active
            if (isNexos) {
              const currentSession = this.$store.getters['session/currentSession'];
              if (currentSession && currentSession !== loginResponse.sessionInfo.id) {
                console.log("currentSession:", currentSession);
                // This is a new session while another is active
                // We should show a warning and handle the session conflict
                this.$bvModal.msgBoxConfirm(
                  'Another session is already active. This will log out the other session. Do you want to continue?',
                  {
                    title: 'Session Conflict',
                    okVariant: 'danger',
                    okTitle: 'Yes, continue',
                    cancelTitle: 'No, stay logged out'
                  }
                ).then(value => {
                  console.log("value from msgBoxConfirm:", value);
                  if (value) {
                    // User confirmed, proceed with new session
                    this.$store.dispatch('session/updateSessionInfo', loginResponse.sessionInfo);
                    console.log('[DEBUG] About to call pushNexosDataLayer from session conflict flow');
                    this.pushNexosDataLayer(loginResponse, this.$store);
                    this.handleRedirect();
                  } else {
                    // User cancelled, clear the auth state and redirect to login
                    console.log("removing auth state cookie...");
                    Vue.$cookies.remove(AUTH_STATE_COOKIE);
                    window.location.href = process.env.VUE_APP_MY_NECTIOS_URL;
                  }
                });
                return;
              }
            }
            
            // Normal flow - update session info
            this.$store.dispatch('session/updateSessionInfo', loginResponse.sessionInfo);
          } // END if loginResponse.sessionInfo

          if(isNexos) {
            if (loginResponse.useBearer) {
              console.log('loginResponse.useBearer is true');
            } else {
              if (fromRegister) {
                console.log('fromRegister is true, redirecting to login page...');
                // Redirect to the login page
                const route = { name: 'auth-login' };
                if (this.communitySlug) {
                  route.params = { communityId: this.communitySlug };
                }
                route.query = { registered: 'true' };
                console.log('redirecting to route:', route);
                this.$router.push(route);
                return;
              } else {
                console.log("coming from login, saving cookie with logged in state...");
                // Save cookie with logged in state
                Vue.$cookies.set(AUTH_STATE_COOKIE, 'authenticated', {
                  httpOnly: false,
                  domain: getDomain()
                });
                console.log('[DEBUG] About to call pushNexosDataLayer from normal login flow');
                this.pushNexosDataLayer(loginResponse, this.$store);
              
                // For "OK" response, we only handle redirection
                console.log('and redirecting inside with handleRedirect...');
                await this.handleRedirect();
                return;
              }
            }
          }
        } // END if loginResponseIsObjectWithMessageOK

        // Checking token in response...

        // console.log('loginResponse.token:', loginResponse.token);
        // Validate token format for JWT
        if (!loginResponse.token) {
          throw new Error('Invalid token: token is required');
        }

        // Handle both string and JSON token formats
        const tokenString = typeof loginResponse === 'string' ? loginResponse : loginResponse.token;
        if (!tokenString || tokenString.split('.').length !== 3) {
          throw new Error('Invalid JWT format');
        }
        // console.log('tokenString:', tokenString);

        // Set cookie expiry
        const date = new Date();
        date.setDate(date.getDate() + expiryDays);

        const isProduction = process.env.NODE_ENV === 'production' || process.env.NODE_ENV === 'staging';

        // Get parent domain for cross-subdomain sharing
        const parentDomain = getDomain();
        const domain = parentDomain ? `.${parentDomain}` : (process.env.VUE_APP_DOMAIN_1 || 'localhost');
        
        // Remove any 'app.' prefix from the domain if it exists
        const cleanDomain = domain.startsWith('.app.') ? domain.replace('.app.', '.') : domain;

        // Base cookie options
        const baseOptions = {
            path: '/',
            domain: cleanDomain,
            expires: date,
            secure: true,
            sameSite: 'Lax',  // Must be Lax for cross-origin requests
            priority: 'High'
        };

        // console.log('Setting cookies with cross-origin compatible options:', JSON.stringify(baseOptions));

        // Set cookies with explicit options for each one
        const setCookie = (name, value, additionalOptions = {}) => {
            const options = {
                ...baseOptions,
                ...additionalOptions
            };
            
            // Force critical security options while maintaining cross-origin compatibility
            options.secure = true;  // Always use HTTPS
            options.sameSite = 'Lax';  // Required for cross-origin
            options.priority = 'High';
            
            Vue.$cookies.set(name, value, options);
            
            // Verify cookie was set
            const cookie = Vue.$cookies.get(name);
            if (!cookie) {
                console.error(`Failed to set cookie: ${name}`);
            }
        };

        // console.log('setting cookie with access token...');
        // Store the complete token with specific options
        setCookie(AccessTokenKey, tokenString, {
            httpOnly: false // We need JS access for API calls
        });

        // Set auth state cookie
        setCookie(AUTH_STATE_COOKIE, 'authenticated', {
            httpOnly: false,
            domain: getDomain()
        });

        // Set CSRF token - this is especially important for cross-origin requests
        const csrfToken = generateRandomToken();
        setCookie('XSRF-TOKEN', csrfToken, {
            httpOnly: false
        });

        // Log successful cookie setup
        // console.log('All cookies set successfully with cross-origin settings');

        // DataLayer push for Nexos (UserID and mdm)
        console.log('[DEBUG] About to call pushNexosDataLayer from final flow');
        this.pushNexosDataLayer(loginResponse, this.$store);

        // Handle redirection based on community type
        await this.handleRedirect();
      } catch (error) {
        console.error('Error in handleAuthSuccess:', error);
        throw error;
      }
    },

    /**
     * Generates a SHA256 hash of an email address
     * @param {string} email - The email address to hash
     * @returns {string} The SHA256 hash in hexadecimal format
     */
    hashEmail(email) {
      if (!email || typeof email !== 'string') {
        return null;
      }
      
      try {
        // Convert email to lowercase as per the specification
        const normalizedEmail = email.toLowerCase();
        
        // Generate SHA256 hash using CryptoJS as shown in the specification
        const hex = CryptoJS.SHA256(normalizedEmail).toString(CryptoJS.enc.Hex);
        
        return hex;
      } catch (error) {
        console.error('Error hashing email:', error);
        return null;
      }
    },

    /**
     * Pushes Nexos userId and mdm to the dataLayer for analytics
     * @param {Object} loginResponse - The authentication response
     * @param {Object} store - The Vuex store instance (usually this.$store)
     */
    pushNexosDataLayer(loginResponse, store) {
      console.log('[DEBUG] pushNexosDataLayer called with:', { loginResponse, store });
      const isNexos = process.env.VUE_APP_CUSTOMER === 'nexos';
      // console.log('[DEBUG] isNexos:', isNexos);
      if (!isNexos) return null;
      let username = null;
      let mdm = null;
      let userId = null;
      let email = null;
      let tipo_doc = null;
      let telefono = null;
      let hashedEmail = null;

      // 1. From login response
      if (loginResponse && loginResponse.user) {
        console.log('[DEBUG] Extracting from loginResponse.user:', loginResponse.user);
        username = loginResponse.user.username || null;
        userId = loginResponse.user.userId || null;
        email = loginResponse.user.email || null;
        tipo_doc = loginResponse.user.tipo_doc || null;
        telefono = loginResponse.user.telefono || null;
        mdm = loginResponse.user.mdm || null;
        console.log('[DEBUG] After loginResponse extraction - email:', email);
        
        // Hash email if we have it from login response
        if (email) {
          hashedEmail = this.hashEmail(email);
          console.log('[DEBUG] hashedEmail from loginResponse:', hashedEmail);
        } else {
          console.log('[DEBUG] Skipping email hash from loginResponse - email is null:', email);
        }
      }

      // 2. Only fall back to store if we don't have the data from login response
      if (!username || !userId || !email || !tipo_doc || !telefono) {
        
        const userInStore = store && store.getters && store.getters.loggedUser;
        console.log('[DEBUG] userInStore:', userInStore);
        if (userInStore) {
          console.log('[DEBUG] Extracting from store - before overwrite - email:', email);
          // Only overwrite if we don't have valid data from login response
          if (!username) {
            username = userInStore.username || null;
          }
          if (!userId) {
            userId = userInStore.userId || null;
          }
          if (!email) {
            email = userInStore.email || null;
          }
          console.log('[DEBUG] After store extraction - email:', email);
          // Only hash email if it's valid (not null)
          if (email) {
            hashedEmail = this.hashEmail(email);
            console.log('[DEBUG] hashedEmail:', hashedEmail);
          } else {
            console.log('[DEBUG] Skipping email hash - email is null');
            hashedEmail = null;
          }

          if (!tipo_doc) {
            tipo_doc = userInStore.tipo_doc || null;
          }
          if (!telefono) {
            telefono = userInStore.telefono || null;
          }
          if (!mdm) {
            mdm = userInStore.mdm || null;
          }
        }
      }

      // Only push if you have a valid hashed email
      if (hashedEmail) {
        window.dataLayer = window.dataLayer || [];
        const data = { 
          "event": 'SEND_USERID', 
          // "username": username,
          // "tipo_doc": tipo_doc,
          // "userId": userId,
          // "telefono": telefono,
          "email": hashedEmail,
        };
        console.log('pushNexosDataLayer data:', data);
        if (mdm) data.mdm = mdm;
        window.dataLayer.push(data);
        console.log('pushed data to dataLayer');
      } else {
        console.log('[DEBUG] Skipping dataLayer push - no valid hashed email available');
      }
    },

    /**
     * Handles redirection after successful authentication
     */
    async handleRedirect() {
      const isNexos = process.env.VUE_APP_CUSTOMER === 'nexos';
      // console.log('handleRedirect() START');
      
      const redirectUrl = Vue.$cookies.get('redirectAfterLogin');
      if (redirectUrl) {
        Vue.$cookies.remove('redirectAfterLogin');
        // Use router if it's a relative path, otherwise use window.location
        if (redirectUrl.startsWith('/') || redirectUrl.startsWith('./')) {
          this.$router.push(redirectUrl);
        } else {
          window.location.href = redirectUrl;
        }
        return;
      }

      // Use Vue router for internal navigation to avoid full page reload
      // This ensures proper route matching and prevents catch-all redirects
      if (this.communitySlug && this.$router) {
        const validSlug = typeof this.communitySlug === 'object' ? 
          (this.communitySlug.toString() || '') : 
          this.communitySlug;
        
        // Navigate directly to the Resumen (Home) route by name to avoid catch-all redirects
        // This ensures we go to /nexos/ (Home) instead of /nexos/welcome
        try {
          console.log('handleRedirect: Navigating to Resumen route for community:', validSlug);
          this.$router.push({ 
            name: 'Resumen',
            params: { communityId: validSlug }
          });
          return;
        } catch (error) {
          console.warn('handleRedirect: Failed to navigate by route name, falling back to path:', error);
          // Fallback to path-based navigation
          const communityPath = `/${validSlug}`;
          this.$router.push(communityPath);
          return;
        }
      }

      // Fallback to window.location for external URLs or when router is not available
      const vueAppUrl = process.env.VUE_APP_APP_URL_1 || 'http://localhost:8080';
      // console.log('vueAppUrl:', vueAppUrl);

      let destinationPath = '';
      
      if (this.communitySlug) {
        const validSlug = typeof this.communitySlug === 'object' ? 
          (this.communitySlug.toString() || '') : 
          this.communitySlug;
        destinationPath = `/${validSlug}`;
      }

      const destinationURL = this.getValidPath(vueAppUrl + destinationPath);
      // console.log('destinationURL:', destinationURL);

      // Check if destinationURL is a valid URL
      try {
        const url = new URL(destinationURL);
        // console.log('validated url:', url);
      } catch (error) {
        console.error('Invalid destination URL:', destinationURL);
        return;
      }

      // Redirect to the destination URL
      window.location.href = destinationURL;
    },

    /**
     * Ensures path is valid and safe
     */
    getValidPath(path) {
      if (typeof path === 'object') {
        console.error('Invalid path object:', path);
        return '/';
      }
      return path;
    }
  }
}; 